import type { tableSettingsProps } from "../useTableSettings";

const toArray = <T,>(value?: T | T[] | null): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const nextFrame = () => new Promise<void>((resolve) => {
  if (typeof window !== "undefined" && window.requestAnimationFrame) {
    window.requestAnimationFrame(() => resolve());
    return;
  }
  setTimeout(resolve, 0);
});

const isEditMode = (mode: any) => {
  const raw = mode?.mode ?? mode;
  return String(raw).toLowerCase() === "edit";
};

export const hasTableChanges = (tableSettings?: tableSettingsProps, additionalEdits?: AdditionalTableEdits | AdditionalTableEdits[],) => {
  const extras = toArray(additionalEdits);

  return (
    Boolean(tableSettings?.hasUnsavedRows) ||
    extras.some((x) => Boolean(x?.hasChanges))
  );
};

export const commitPendingGridEdit = async (tableSettings?: tableSettingsProps,) => {
  const api: any = tableSettings?.apiRef?.current;
  if (!api) return;

  const focusedCell = api?.state?.focus?.cell;
  const editMode = tableSettings?.editMode ?? "row";

  let stopped = false;

  const rowIds = new Set<any>();

  if (focusedCell?.id != null) {
    rowIds.add(focusedCell.id);
  }

  Object.entries(tableSettings?.rowModes ?? {}).forEach(([id, model]) => {
    if (isEditMode(model)) {
      rowIds.add(id);
    }
  });

  if (editMode === "row") {
    for (const id of rowIds) {
      try {
        if (!api.getRowMode || api.getRowMode(id) === "edit") {
          api.stopRowEditMode?.({ id, ignoreModifications: false, });

          stopped = true;
        }
      } catch {
        // The row may already be out of edit mode.
      }
    }
  }

  if (!stopped && focusedCell?.id != null && focusedCell?.field != null) {
    stopped = safeStopCellEditMode(api, focusedCell.id, focusedCell.field, false,);
  }

  await Promise.resolve();
  await nextFrame();
};

export const commitPendingTableEdits = async (tableSettings?: tableSettingsProps, additionalEdits?: AdditionalTableEdits | AdditionalTableEdits[],) => {
  const extras = toArray(additionalEdits);
  for (const extra of extras) {
    await extra?.commitPendingEdits?.();
  }
  await commitPendingGridEdit(tableSettings);
};

export const discardTableEdits = (tableSettings?: tableSettingsProps, additionalEdits?: AdditionalTableEdits | AdditionalTableEdits[],) => {
  const extras = toArray(additionalEdits);
  extras.forEach((extra) => extra?.clearChanges?.());
  tableSettings?.discardAllChanges?.();
};

export const getTableSubmitRows = (tableSettings?: tableSettingsProps, additionalEdits?: AdditionalTableEdits | AdditionalTableEdits[],) => {
  const extras = toArray(additionalEdits);
  const changedRows = tableSettings?.changedRowsRef?.current ?? [];
  const additionalRows = extras.flatMap((extra) => {
    const rows = extra?.getChanges?.();
    return Array.isArray(rows) ? rows : [];
  });
  return [...changedRows, ...additionalRows];
};

export const safeStopCellEditMode = (api: any, id: any, field: string, ignoreModifications = false,) => {
  if (!api || id == null || !field) return false;
  try {
    if (api.getCellMode && api.getCellMode(id, field) !== "edit") {
      return false;
    }
    api.stopCellEditMode?.({ id, field, ignoreModifications, });
    return true;
  } catch (error: any) {
    const message = String(error?.message ?? error);
    if (message.includes("not in edit mode")) {
      return false;
    }
    throw error;
  }
};

export const commitExternalRowUpdate = (tableSettings: tableSettingsProps | undefined, updatedRow: any, originalRow: any,) => {
  if (!updatedRow) return updatedRow;

  return (
    tableSettings?.processRowUpdateRef?.current?.(updatedRow, originalRow) ??
    updatedRow
  );
};

export interface AdditionalTableEdits {
  hasChanges?: boolean;
  commitPendingEdits?: () => void | Promise<void>;
  clearChanges?: () => void;
  getChanges?: () => any[];
}
